/**
 * Owner: Masa Bayu
 * File: language-id.ts
 * Date:Time: 2026-09-19 15:48
 * Description: BCP 47–aligned languageId parse, normalize, validate (GL-01).
 */
import { resolvePrimaryAlias } from './aliases';
import { QxLanguageError } from './errors';
/** Loose BCP 47 language tag shape (interoperability, not full IANA registry). */
const LANGUAGE_TAG_PATTERN = /^[A-Za-z]{2,8}(-[A-Za-z]{4})?(-[A-Za-z]{2}|-[0-9]{3})?(-[A-Za-z0-9]{5,8}|-[0-9][A-Za-z0-9]{3})*(-[A-WY-Za-wy-z0-9]-[A-Za-z0-9]{2,8})*(-x-[A-Za-z0-9]{1,8})?$/;
export function isValidLanguageIdShape(value) {
    return LANGUAGE_TAG_PATTERN.test(value.trim());
}
/**
 * Normalize to canonical storage form:
 * primary lowercase · script Title case · region UPPERCASE · resolve primary aliases.
 */
export function normalizeLanguageId(raw) {
    const trimmed = raw.trim();
    if (trimmed.length === 0 || /\s/.test(trimmed)) {
        throw new QxLanguageError('INVALID_LANGUAGE_ID', 'languageId must be non-empty without whitespace');
    }
    if (!isValidLanguageIdShape(trimmed)) {
        throw new QxLanguageError('INVALID_LANGUAGE_ID', `invalid BCP 47–like languageId: ${raw}`);
    }
    const parts = trimmed.split('-');
    const primaryRaw = parts[0];
    const alias = resolvePrimaryAlias(primaryRaw);
    const primary = alias.canonical;
    let index = 1;
    let script = null;
    let region = null;
    const variants = [];
    let privateUse = null;
    if (index < parts.length && /^[A-Za-z]{4}$/.test(parts[index])) {
        script = titleCaseScript(parts[index]);
        index += 1;
    }
    if (index < parts.length && (/^[A-Za-z]{2}$/.test(parts[index]) || /^[0-9]{3}$/.test(parts[index]))) {
        region = parts[index].toUpperCase();
        index += 1;
    }
    while (index < parts.length) {
        const part = parts[index];
        if (part.toLowerCase() === 'x') {
            privateUse = parts.slice(index).join('-').toLowerCase();
            break;
        }
        if (/^[0-9][A-Za-z0-9]{3}$/.test(part) || /^[A-Za-z0-9]{5,8}$/.test(part)) {
            variants.push(part.toLowerCase());
            index += 1;
            continue;
        }
        if (/^[A-WY-Za-wy-z0-9]$/.test(part) && index + 1 < parts.length) {
            // extension singleton — keep lowercase joined remainder for identity stability
            variants.push(parts.slice(index).join('-').toLowerCase());
            break;
        }
        throw new QxLanguageError('INVALID_LANGUAGE_ID', `unrecognized subtag in languageId: ${raw}`);
    }
    const languageId = buildLanguageId(primary, script, region, variants, privateUse);
    return Object.freeze({
        languageId,
        primary,
        script,
        region,
        variants: Object.freeze([...variants]),
        privateUse,
        deprecatedFrom: alias.deprecatedFrom,
    });
}
export function assertLanguageId(raw) {
    return normalizeLanguageId(raw).languageId;
}
function titleCaseScript(script) {
    const lower = script.toLowerCase();
    return `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
}
function buildLanguageId(primary, script, region, variants, privateUse) {
    const parts = [primary];
    if (script) {
        parts.push(script);
    }
    if (region) {
        parts.push(region);
    }
    for (const variant of variants) {
        parts.push(variant);
    }
    if (privateUse) {
        parts.push(privateUse);
    }
    return parts.join('-');
}
//# sourceMappingURL=language-id.js.map